// Bộ tính chương trình khuyến mãi (mua kèm giá ưu đãi + quà tặng theo giá trị đơn) — hàm thuần, KHÔNG đọc DB.
// Mirror của healthymall-be/src/utils/promotionEngine.js — giỏ hàng dùng bản này để HIỂN THỊ, máy chủ dùng bản kia để TÍNH TIỀN.
// Sửa quy tắc ở đây thì sửa y hệt bên kia.
//
// baseDetails: [{ _id, name, discountPrice (đơn giá thực, đã gồm flash sale), quantity, ...trường SP }] — 1 dòng / sản phẩm
// programs:    bản ghi Promotion (model) — tự lọc chương trình đang chạy theo isActive/startAt/endAt
// giftProducts: { [productId]: { _id, name, sku, url, category, brand, images, coverImage, salePrice, discountPrice, stock, isStop, isPreOrder } }
// Trả về { details, subtotal, applied, giftNudge }:
//   details  — dòng đã tách phần giá mua kèm (promotionId, noBulk) + dòng quà (isGift, giá 0)
//   subtotal — tạm tính hàng mua (không gồm quà)
//   applied  — chương trình đã áp: [{ programId, name, type, productId, quantity, price?, saved, outOfStock? }]
//   giftNudge — chương trình quà gần đạt nhất: { programId, name, minOrder, missing, productId, quantity } | null

export const isLive = (p, now) =>
  Boolean(p?.isActive) && (!p.startAt || new Date(p.startAt) <= now) && (!p.endAt || new Date(p.endAt) >= now);

const unitOf = (p) => (Number(p?.discountPrice) > 0 ? Number(p.discountPrice) : Number(p?.salePrice) || 0);

export const applyPromotions = (baseDetails, programs, giftProducts = {}, now = new Date()) => {
  let details = (Array.isArray(baseDetails) ? baseDetails : []).map((d) => ({ ...d }));
  const live = (Array.isArray(programs) ? programs : []).filter((p) => isLive(p, now)).sort((a, b) => (b.priority || 0) - (a.priority || 0));
  const applied = [];

  // 1) Mua kèm giá ưu đãi
  live
    .filter((p) => p.type === "bundle")
    .forEach((p) => {
      const triggers = (p.triggerProductIds || []).map(String);
      const target = String(p.targetProductId || "");
      const price = Number(p.price);
      if (!triggers.length || !target || triggers.includes(target) || !(price >= 0)) return;
      const triggerQty = details.filter((d) => !d.isGift && triggers.includes(String(d._id))).reduce((s, d) => s + (Number(d.quantity) || 0), 0);
      let allow = triggerQty * (Number(p.perTrigger) > 0 ? Number(p.perTrigger) : 1);
      if (Number(p.maxPerOrder) > 0) allow = Math.min(allow, Number(p.maxPerOrder));
      if (allow <= 0) return;
      const idx = details.findIndex((d) => !d.isGift && !d.promotionId && String(d._id) === target);
      if (idx < 0) return;
      const line = details[idx];
      const regular = Number(line.discountPrice) || 0;
      if (!(price < regular)) return; // giá đang bán (vd. flash sale) đã rẻ hơn → giữ giá tốt hơn cho khách
      const quantity = Math.min(allow, Number(line.quantity) || 0);
      const promoLine = {
        ...line,
        quantity,
        discountPrice: price,
        regularPrice: regular,
        promotionId: String(p._id),
        promotionName: p.name,
        noBulk: !p.stackBulk,
      };
      const rest = (Number(line.quantity) || 0) - quantity;
      details.splice(idx, 1, ...(rest > 0 ? [promoLine, { ...line, quantity: rest }] : [promoLine]));
      applied.push({ programId: String(p._id), name: p.name, type: "bundle", productId: target, quantity, price, saved: (regular - price) * quantity });
    });

  const subtotal = details.filter((d) => !d.isGift).reduce((s, d) => s + (Number(d.discountPrice) || 0) * (Number(d.quantity) || 0), 0);

  // 2) Quà tặng theo giá trị đơn (xét trên tạm tính hàng mua, trước giảm 5% / voucher)
  let giftNudge = null;
  live
    .filter((p) => p.type === "gift")
    .forEach((p) => {
      const min = Number(p.minOrder) || 0;
      const prod = giftProducts[String(p.giftProductId)];
      if (!prod || prod.isStop) return;
      const per = Number(p.giftQuantity) > 0 ? Number(p.giftQuantity) : 1;
      if (subtotal < min) {
        const missing = min - subtotal;
        if (!giftNudge || missing < giftNudge.missing) {
          giftNudge = { programId: String(p._id), name: p.name, minOrder: min, missing, productId: String(prod._id), quantity: per };
        }
        return;
      }
      let quantity = per * (p.repeat && min > 0 ? Math.floor(subtotal / min) : 1);
      const bought = details.filter((d) => String(d._id) === String(prod._id)).reduce((s, d) => s + (Number(d.quantity) || 0), 0);
      const available = prod.isPreOrder ? quantity : Math.max(0, (Number(prod.stock) || 0) - bought);
      quantity = Math.min(quantity, available);
      if (quantity <= 0) {
        applied.push({ programId: String(p._id), name: p.name, type: "gift", productId: String(prod._id), quantity: 0, saved: 0, outOfStock: true });
        return;
      }
      details.push({
        _id: String(prod._id),
        name: prod.name,
        sku: prod.sku,
        url: prod.url,
        category: prod.category,
        brand: prod.brand,
        images: prod.images,
        coverImage: prod.coverImage,
        salePrice: prod.salePrice,
        discountPrice: 0,
        regularPrice: unitOf(prod),
        quantity,
        isGift: true,
        noBulk: true,
        promotionId: String(p._id),
        promotionName: p.name,
      });
      applied.push({ programId: String(p._id), name: p.name, type: "gift", productId: String(prod._id), quantity, saved: unitOf(prod) * quantity });
    });

  return { details, subtotal, applied, giftNudge };
};


/** Giỏ hàng [{ product, quantity }] → dòng gốc cho engine (đơn giá thực = giá đang bán, giống backend priceCart). */
export const cartToBaseDetails = (cartItems) =>
  (Array.isArray(cartItems) ? cartItems : [])
    .filter((i) => i?.product?._id && Number(i?.quantity) > 0)
    .map((i) => ({ ...i.product, _id: String(i.product._id), discountPrice: unitOf(i.product), quantity: Number(i.quantity) }));

/** Tính thử giỏ hàng với chương trình đang chạy (data = kết quả /promotion/get-active). */
export const priceCartLocal = (cartItems, data) =>
  applyPromotions(cartToBaseDetails(cartItems), data?.programs ?? [], data?.products ?? {});
